// DJ-mixer style horizontal slider. One slider per row: a label on the
// left, value readout on the right, with a green LED ladder + white
// centerline + grey cap along the horizontal track in the middle.
// Horizontal drag, ~140px range — sized for thumb travel without going
// too wide on phones.
//
// API mirrors Knob exactly: { min, max, step, value, default, label, format,
// addEventListener('change', fn), getValue(), setValue(v, {silent}), mount(parent),
// destroy() }. Drop-in replacement for any Knob calling site.

const DRAG_RANGE_PX = 140;
const TRACK_LENGTH_PX = 160;
const LED_COUNT = 10;

export class Slider {
  constructor({
    label = '',
    min = 0,
    max = 1,
    step = 0.01,
    value = 0,
    default: defaultValue = value,
    format = (v) => v.toFixed(2),
  } = {}) {
    this.label = label;
    this.min = min;
    this.max = max;
    this.step = step;
    this._value = this._clamp(value);
    this.default = this._clamp(defaultValue);
    this.format = format;
    this._listeners = { change: [] };

    this._dragging = false;
    this._dragStartX = 0;
    this._dragStartValue = 0;
    this._pointerId = null;

    this._build();
  }

  _build() {
    this.root = document.createElement('div');
    this.root.className = 'dj-slider';
    this.root.tabIndex = 0;

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'dj-slider-label';
    this.labelEl.textContent = this.label;

    this.track = document.createElement('div');
    this.track.className = 'dj-slider-track';

    // White centerline running across the track — the visual "fader rail"
    this.centerline = document.createElement('div');
    this.centerline.className = 'dj-slider-centerline';
    this.track.appendChild(this.centerline);

    // LED tick marks underneath the track (one row of LEDs that fill L → R)
    this.ledsEl = document.createElement('div');
    this.ledsEl.className = 'dj-slider-leds';
    this._leds = [];
    for (let i = 0; i < LED_COUNT; i++) {
      const led = document.createElement('div');
      led.className = 'dj-slider-led';
      this.ledsEl.appendChild(led);
      this._leds.push(led);
    }
    this.track.appendChild(this.ledsEl);

    // Cap (the draggable handle) — vertical bar
    this.cap = document.createElement('div');
    this.cap.className = 'dj-slider-cap';
    this.track.appendChild(this.cap);

    this.valueEl = document.createElement('div');
    this.valueEl.className = 'dj-slider-value';
    this.valueEl.textContent = this.format(this._value);

    this.root.appendChild(this.labelEl);
    this.root.appendChild(this.track);
    this.root.appendChild(this.valueEl);

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onDblClick = this._onDblClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    this.track.addEventListener('pointerdown', this._onPointerDown);
    this.track.addEventListener('dblclick', this._onDblClick);
    this.root.addEventListener('keydown', this._onKeyDown);

    this._render();
  }

  mount(parentEl) {
    parentEl.appendChild(this.root);
    return this.root;
  }

  destroy() {
    this.track.removeEventListener('pointerdown', this._onPointerDown);
    this.track.removeEventListener('dblclick', this._onDblClick);
    this.root.removeEventListener('keydown', this._onKeyDown);
    if (this._dragging) this._onPointerUp({ pointerId: this._pointerId });
    this._listeners.change = [];
    this.root.remove();
  }

  getValue() {
    return this._value;
  }

  setValue(v, { silent = false } = {}) {
    const next = this._clamp(this._snap(v));
    if (next === this._value) {
      this._render();
      return;
    }
    this._value = next;
    this._render();
    if (!silent) this._emit('change', { value: this._value });
  }

  getNormalized() {
    return (this._value - this.min) / (this.max - this.min);
  }

  addEventListener(type, fn) {
    if (!this._listeners[type]) this._listeners[type] = [];
    this._listeners[type].push(fn);
  }

  removeEventListener(type, fn) {
    if (!this._listeners[type]) return;
    this._listeners[type] = this._listeners[type].filter((f) => f !== fn);
  }

  _emit(type, payload) {
    (this._listeners[type] || []).forEach((fn) => {
      try { fn(payload); } catch (err) { console.error('[slider] listener threw', err); }
    });
  }

  _clamp(v) {
    if (!Number.isFinite(v)) return this.min;
    return Math.max(this.min, Math.min(this.max, v));
  }

  _snap(v) {
    if (!this.step || this.step <= 0) return v;
    const n = Math.round((v - this.min) / this.step);
    return this.min + n * this.step;
  }

  _render() {
    const t = this.getNormalized();
    // Cap horizontal position: 0% → 100% along the track.
    this.cap.style.left = `calc(${t * 100}% - 7px)`;
    // LEDs fill from left to right — light count = round(t * LED_COUNT)
    const litCount = Math.round(t * LED_COUNT);
    for (let i = 0; i < LED_COUNT; i++) {
      this._leds[i].classList.toggle('lit', i < litCount);
    }
    this.valueEl.textContent = this.format(this._value);
  }

  _onPointerDown(e) {
    if (this._dragging) return;
    this._dragging = true;
    this._dragStartX = e.clientX;
    this._dragStartValue = this._value;
    this._pointerId = e.pointerId;
    try { this.track.setPointerCapture(e.pointerId); } catch (_) { /* test envs */ }
    this.track.addEventListener('pointermove', this._onPointerMove);
    this.track.addEventListener('pointerup', this._onPointerUp);
    this.track.addEventListener('pointercancel', this._onPointerUp);
    this.root.focus();
    e.preventDefault?.();
  }

  _onPointerMove(e) {
    if (!this._dragging) return;
    const dx = e.clientX - this._dragStartX; // right = positive
    const range = this.max - this.min;
    const delta = (dx / DRAG_RANGE_PX) * range;
    this.setValue(this._dragStartValue + delta);
  }

  _onPointerUp(e) {
    if (!this._dragging) return;
    this._dragging = false;
    try { this.track.releasePointerCapture(e.pointerId); } catch (_) { /* test envs */ }
    this.track.removeEventListener('pointermove', this._onPointerMove);
    this.track.removeEventListener('pointerup', this._onPointerUp);
    this.track.removeEventListener('pointercancel', this._onPointerUp);
  }

  _onDblClick() {
    this.setValue(this.default);
  }

  _onKeyDown(e) {
    const step = this.step || 0.01;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      this.setValue(this._value + step);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      this.setValue(this._value - step);
      e.preventDefault();
    } else if (e.key === 'PageUp') {
      this.setValue(this._value + step * 10);
      e.preventDefault();
    } else if (e.key === 'PageDown') {
      this.setValue(this._value - step * 10);
      e.preventDefault();
    } else if (e.key === 'Home') {
      this.setValue(this.min);
      e.preventDefault();
    } else if (e.key === 'End') {
      this.setValue(this.max);
      e.preventDefault();
    }
  }
}

export const SLIDER_TRACK_LENGTH_PX = TRACK_LENGTH_PX;
export const SLIDER_DRAG_RANGE_PX = DRAG_RANGE_PX;
export const SLIDER_LED_COUNT = LED_COUNT;
